import { getMedicationPrimaryDoseLabel } from "../features/medications/medicationDosePresentation";
import { CARE_SNAPSHOT_STEPS } from "../constants/careSnapshotSections";
import { normalizeCareFocusSelection } from "./careFocusSelection";
import { CARE_LENS, getCareLensKey } from "./careLens";
import { PROFILE_CHAPTER_TITLES } from "../constants/profileChapterTitles";

export const CARE_PROFILE_SECTION_IDS = {
  ABOUT: "about",
  MEDICATION: "medication",
  ALLERGIES_FOOD: "allergies_food",
  CARE_TEAM: "care_team",
  DAILY_SUPPORT: "daily_support",
  GOALS: "goals",
  BEHAVIOR_SUPPORT: "behavior_support",
  SENSORY_PROFILE: "sensory_profile",
  ROUTINE_TRANSITIONS: "routine_transitions",
  COMMUNICATION: "communication",
  MEMORY_ORIENTATION: "memory_orientation",
  MOBILITY_SAFETY: "mobility_safety",
  SLEEP: "sleep",
  SYMPTOMS_MONITORING: "symptoms_monitoring",
  EMERGENCY_PROTOCOL: "emergency_protocol",
  COMFORT_CARE: "comfort_care",
  CARE_WISHES: "care_wishes",
  DAILY_COMFORT_ROUTINE: "daily_comfort_routine",
};

export const getCareProfileOverviewSections = (profileOverview = {}) => [
  ...(profileOverview.coreSections || []),
  ...(profileOverview.adaptiveGroup?.sections || []),
];

export const getCareProfileOverviewCompletionSummary = (profileOverview = {}) => {
  const sections = getCareProfileOverviewSections(profileOverview);
  const total = sections.length;
  const complete = sections.filter((section) => section.hasDetails).length;
  const percent = total ? Math.round((complete / total) * 100) : 0;
  const remaining = Math.max(0, total - complete);

  return {
    total,
    complete,
    percent,
    remaining,
    label: remaining
      ? `${complete} of ${total} sections complete`
      : `${total} of ${total} sections complete`,
  };
};

const normalizeItems = (value = []) => {
  const values = Array.isArray(value) ? value : [value];
  return values
    .map((item) => {
      if (typeof item === "string") return item.trim();
      return String(item?.label || item?.name || item?.title || item?.value || "").trim();
    })
    .filter(Boolean);
};

const hasAnyValue = (value) => {
  if (Array.isArray(value)) return value.some(hasAnyValue);
  if (value && typeof value === "object") return Object.values(value).some(hasAnyValue);
  return String(value || "").trim().length > 0;
};

const getMedicationNames = (medicalProfile = {}) => {
  const medicationRecords = Array.isArray(medicalProfile.medicationRecords)
    ? medicalProfile.medicationRecords
      .filter((entry) => entry?.name && !entry.isArchived)
      .map((entry) => {
        const dose = getMedicationPrimaryDoseLabel({ ...entry, dose: entry.dose || entry.strength });
        return dose ? `${entry.name} ${dose}` : entry.name;
      })
    : [];

  return medicationRecords;
};

const getCareFocusItems = ({ selectedConditions = [], careContext = {} } = {}) => [
  ...normalizeItems(normalizeCareFocusSelection(selectedConditions)),
  ...normalizeItems(careContext.careFocus),
  ...normalizeItems(careContext.trackingFocus),
];

const resolveAdaptiveGroupLabel = (group, careFocusItems = []) => {
  if (group.key !== "chronic_illness") return group.label;
  const matchingCondition = careFocusItems.find((item) => {
    const text = item.toLowerCase();
    return text.includes("multiple sclerosis") || text === "ms";
  });
  return matchingCondition && matchingCondition.toLowerCase() !== "ms" ? matchingCondition : group.label;
};

const buildSection = ({ id, label, description, targetStep, value, tone = "support" }) => {
  const hasDetails = hasAnyValue(value);
  return { id, label, description, targetStep, tone, hasDetails, statusLabel: hasDetails ? "Added" : "Add when ready", actionLabel: hasDetails ? "Edit" : "Add" };
};

const CORE_SECTION_DEFINITIONS = [
  {
    id: CARE_PROFILE_SECTION_IDS.ABOUT,
    label: "About",
    description: "Identity, care focus, care setting, and emergency contacts.",
    targetStep: CARE_SNAPSHOT_STEPS.ABOUT,
    tone: "about",
    value: ({ name, age, photoURL, selectedConditions, supportProfile }) => [
      name,
      age,
      photoURL,
      selectedConditions,
      supportProfile.about,
      supportProfile.emergencyContacts,
      supportProfile.whoTheyAre,
    ],
  },
  {
    id: CARE_PROFILE_SECTION_IDS.MEDICATION,
    label: PROFILE_CHAPTER_TITLES.MEDICATIONS,
    description: "Current medications and routine medication context.",
    targetStep: CARE_SNAPSHOT_STEPS.HEALTH_CARE,
    tone: "medical",
    value: ({ medicalProfile }) => getMedicationNames(medicalProfile),
  },
  {
    id: CARE_PROFILE_SECTION_IDS.ALLERGIES_FOOD,
    label: PROFILE_CHAPTER_TITLES.ALLERGIES,
    description: "Critical allergies, sensitivities, foods to avoid, and preferences.",
    targetStep: CARE_SNAPSHOT_STEPS.HEALTH_CARE,
    tone: "allergy",
    value: ({ medicalProfile, supportProfile }) => [
      medicalProfile.foodAllergies,
      medicalProfile.dietaryRestrictions,
      supportProfile.whoTheyAre?.favoriteFoods,
    ],
  },
  {
    id: CARE_PROFILE_SECTION_IDS.DAILY_SUPPORT,
    label: "Daily Support",
    description: "Communication style, pacing, environment, and what helps.",
    targetStep: CARE_SNAPSHOT_STEPS.DAILY_SUPPORT,
    tone: "support",
    value: ({ medicalProfile, supportProfile }) => [
      medicalProfile.communicationNeeds,
      supportProfile.communicationStyle,
      supportProfile.communicationNotes,
      supportProfile.whatHelps,
      supportProfile.thingsToAvoid,
    ],
  },
  {
    id: CARE_PROFILE_SECTION_IDS.CARE_TEAM,
    label: "Care Team",
    description: "Care circle members and trusted support contacts.",
    targetStep: null,
    tone: "team",
    value: ({ careTeamMemberCount }) => careTeamMemberCount > 0 ? careTeamMemberCount : "",
  },
  {
    id: CARE_PROFILE_SECTION_IDS.GOALS,
    label: "Goals",
    description: "Care priorities, goals, and support strategies.",
    targetStep: CARE_SNAPSHOT_STEPS.GOALS_GROWTH,
    tone: "goals",
    value: ({ supportProfile }) => [
      supportProfile.careGoals,
      supportProfile.familySupportTips,
      supportProfile.schoolTherapySuggestions,
    ],
  },
];

const ADAPTIVE_GROUPS = [
  {
    key: "autism",
    label: "Autism",
    sections: [
      {
        id: CARE_PROFILE_SECTION_IDS.BEHAVIOR_SUPPORT,
        label: "Behavior",
        description: "Triggers, warning signs, what helps, and safety notes.",
        targetStep: CARE_SNAPSHOT_STEPS.PATTERNS_RESPONSES,
        tone: "behavior",
        value: ({ medicalProfile, supportProfile }) => [
          medicalProfile.behavioralTriggers,
          supportProfile.earlyWarningSigns,
          supportProfile.thingsThatUsuallyHelp,
          supportProfile.thingsToAvoid,
          supportProfile.safetyConsiderations,
          supportProfile.whatCanBeDifficult,
        ],
      },
      {
        id: CARE_PROFILE_SECTION_IDS.SENSORY_PROFILE,
        label: "Sensory Profile",
        description: "Sensory sensitivities, sensory-seeking needs, and tools.",
        targetStep: CARE_SNAPSHOT_STEPS.PATTERNS_RESPONSES,
        tone: "sensory",
        value: ({ medicalProfile }) => medicalProfile.sensoryIssues,
      },
      {
        id: CARE_PROFILE_SECTION_IDS.ROUTINE_TRANSITIONS,
        label: "Routine & Transitions",
        description: "Routine needs, transition supports, and planning notes.",
        targetStep: CARE_SNAPSHOT_STEPS.DAILY_SUPPORT,
        tone: "routine",
        value: ({ supportProfile }) => [
          supportProfile.whatHelps,
          supportProfile.thingsThatUsuallyHelp,
          supportProfile.thingsToAvoid,
        ],
      },
      {
        id: CARE_PROFILE_SECTION_IDS.COMMUNICATION,
        label: "Communication",
        description: "How this person communicates and how caregivers should respond.",
        targetStep: CARE_SNAPSHOT_STEPS.DAILY_SUPPORT,
        tone: "communication",
        value: ({ medicalProfile, supportProfile }) => [
          medicalProfile.communicationNeeds,
          supportProfile.communicationStyle,
          supportProfile.communicationNotes,
        ],
      },
    ],
  },
  {
    key: "dementia",
    label: "Dementia",
    sections: [
      {
        id: CARE_PROFILE_SECTION_IDS.MEMORY_ORIENTATION,
        label: "Memory & Orientation",
        description: "What remains familiar, confusion patterns, and orientation anchors.",
        targetStep: CARE_SNAPSHOT_STEPS.DAILY_SUPPORT,
        tone: "memory",
        value: ({ supportProfile }) => [
          supportProfile.about,
          supportProfile.whatHelps,
          supportProfile.communicationNotes,
        ],
      },
      {
        id: CARE_PROFILE_SECTION_IDS.BEHAVIOR_SUPPORT,
        label: "Behavior & Agitation",
        description: "Sundowning, triggers, de-escalation, wandering, and safety.",
        targetStep: CARE_SNAPSHOT_STEPS.PATTERNS_RESPONSES,
        tone: "behavior",
        value: ({ medicalProfile, supportProfile }) => [
          medicalProfile.behavioralTriggers,
          supportProfile.whatCanBeDifficult,
          supportProfile.thingsThatUsuallyHelp,
          supportProfile.safetyConsiderations,
        ],
      },
      {
        id: CARE_PROFILE_SECTION_IDS.MOBILITY_SAFETY,
        label: "Mobility & Safety",
        description: "Fall risk, equipment, supervision, and always-on safety needs.",
        targetStep: CARE_SNAPSHOT_STEPS.PATTERNS_RESPONSES,
        tone: "safety",
        value: ({ supportProfile }) => supportProfile.safetyConsiderations,
      },
      {
        id: CARE_PROFILE_SECTION_IDS.SLEEP,
        label: "Sleep",
        description: "Sleep patterns, night safety, and rest supports.",
        targetStep: CARE_SNAPSHOT_STEPS.PATTERNS_RESPONSES,
        tone: "sleep",
        value: ({ medicalProfile }) => medicalProfile.sleepIssues,
      },
    ],
  },
  {
    key: "chronic_illness",
    label: "Chronic Illness",
    sections: [
      {
        id: CARE_PROFILE_SECTION_IDS.SYMPTOMS_MONITORING,
        label: "Symptoms & Monitoring",
        description: "Primary symptoms, warning signs, tracking needs, and patterns.",
        targetStep: CARE_SNAPSHOT_STEPS.HEALTH_CARE,
        tone: "medical",
        value: ({ medicalProfile, supportProfile }) => [
          medicalProfile.sensoryIssues,
          supportProfile.whatCanBeDifficult,
          supportProfile.earlyWarningSigns,
        ],
      },
      {
        id: CARE_PROFILE_SECTION_IDS.EMERGENCY_PROTOCOL,
        label: "Emergency Protocol",
        description: "Relapse signs, escalation steps, and when to seek urgent help.",
        targetStep: CARE_SNAPSHOT_STEPS.PATTERNS_RESPONSES,
        tone: "emergency",
        value: ({ supportProfile }) => supportProfile.safetyConsiderations,
      },
      {
        id: CARE_PROFILE_SECTION_IDS.MOBILITY_SAFETY,
        label: "Mobility & Safety",
        description: "Good-day and bad-day mobility support, equipment, and safety.",
        targetStep: CARE_SNAPSHOT_STEPS.PATTERNS_RESPONSES,
        tone: "safety",
        value: ({ supportProfile }) => supportProfile.safetyConsiderations,
      },
      {
        id: CARE_PROFILE_SECTION_IDS.SLEEP,
        label: "Sleep",
        description: "Sleep patterns, fatigue, rest supports, and overnight concerns.",
        targetStep: CARE_SNAPSHOT_STEPS.PATTERNS_RESPONSES,
        tone: "sleep",
        value: ({ medicalProfile }) => medicalProfile.sleepIssues,
      },
    ],
  },
  {
    key: "hospice",
    label: "Hospice Care",
    sections: [
      {
        id: CARE_PROFILE_SECTION_IDS.COMFORT_CARE,
        label: "Comfort & Symptoms",
        description: "Pain, breathing, nausea, agitation, anxiety, fatigue, and comfort patterns.",
        targetStep: CARE_SNAPSHOT_STEPS.HEALTH_CARE,
        tone: "medical",
        value: ({ medicalProfile, supportProfile }) => [
          medicalProfile.sensoryIssues,
          supportProfile.whatCanBeDifficult,
          supportProfile.earlyWarningSigns,
          supportProfile.whatHelps,
        ],
      },
      {
        id: CARE_PROFILE_SECTION_IDS.CARE_WISHES,
        label: "Care Wishes",
        description: "Documented wishes, spiritual or cultural needs, and what matters most.",
        targetStep: CARE_SNAPSHOT_STEPS.GOALS_GROWTH,
        tone: "goals",
        value: ({ supportProfile }) => [
          supportProfile.careGoals,
          supportProfile.familySupportTips,
          supportProfile.schoolTherapySuggestions,
          supportProfile.whoTheyAre,
        ],
      },
      {
        id: CARE_PROFILE_SECTION_IDS.EMERGENCY_PROTOCOL,
        label: "Who to Call",
        description: "Hospice agency, nurse line, after-hours contact, and escalation plan.",
        targetStep: CARE_SNAPSHOT_STEPS.PATTERNS_RESPONSES,
        tone: "emergency",
        value: ({ supportProfile }) => [
          supportProfile.emergencyContacts,
          supportProfile.safetyConsiderations,
        ],
      },
      {
        id: CARE_PROFILE_SECTION_IDS.DAILY_COMFORT_ROUTINE,
        label: "Daily Comfort Routine",
        description: "Positioning, mouth care, skin care, environment, and calming supports.",
        targetStep: CARE_SNAPSHOT_STEPS.DAILY_SUPPORT,
        tone: "support",
        value: ({ medicalProfile, supportProfile }) => [
          medicalProfile.communicationNeeds,
          supportProfile.communicationStyle,
          supportProfile.communicationNotes,
          supportProfile.whatHelps,
          supportProfile.thingsToAvoid,
        ],
      },
    ],
  },
];

const buildNeedToKnowItems = ({ medicalProfile = {}, supportProfile = {} } = {}) => {
  const medicationNames = getMedicationNames(medicalProfile);
  const allergies = normalizeItems(medicalProfile.foodAllergies);
  const emergencyContacts = String(supportProfile.emergencyContacts || "").trim();
  const safetyNotes = normalizeItems(supportProfile.safetyConsiderations);
  const communication = [
    ...normalizeItems(supportProfile.communicationStyle),
    ...normalizeItems(medicalProfile.communicationNeeds),
    String(supportProfile.communicationNotes || "").trim(),
  ].filter(Boolean);

  return [
    allergies.length ? {
      id: "allergy",
      label: "Allergy",
      tone: "critical",
      text: allergies.join(" • "),
    } : null,
    medicationNames.length ? {
      id: "medication",
      label: "Medication",
      tone: "medical",
      text: medicationNames.join(" • "),
    } : null,
    safetyNotes.length ? {
      id: "safety",
      label: "Safety",
      tone: "emergency",
      text: safetyNotes.slice(0, 2).join(" • "),
    } : null,
    emergencyContacts ? {
      id: "emergency_contact",
      label: "Emergency contact",
      tone: "emergency",
      text: emergencyContacts,
    } : null,
    communication.length ? {
      id: "communication",
      label: "Support",
      tone: "support",
      text: communication.slice(0, 2).join(" • "),
    } : null,
  ].filter(Boolean).slice(0, 4);
};

export const buildCareProfileOverview = ({
  name = "",
  age = "",
  photoURL = "",
  selectedConditions = [],
  careContext = {},
  medicalProfile = {},
  supportProfile = {},
  careTeamMemberCount = 0,
  includeCareTeamSection = true,
} = {}) => {
  const profile = {
    name,
    age,
    photoURL,
    selectedConditions,
    careContext,
    medicalProfile,
    supportProfile,
    careTeamMemberCount,
  };
  const careFocusItems = getCareFocusItems({ selectedConditions, careContext });
  // Exactly one adaptive group, chosen by the shared care-lens resolver.
  // Additional supports and condition context never select a group.
  const careLensKey = getCareLensKey({ selectedConditions, careContext });
  const adaptiveGroup = careLensKey === CARE_LENS.GENERAL
    ? null
    : ADAPTIVE_GROUPS.find((group) => group.key === careLensKey) || null;
  const coreSections = CORE_SECTION_DEFINITIONS
    .filter((definition) => includeCareTeamSection || definition.id !== CARE_PROFILE_SECTION_IDS.CARE_TEAM)
    .map((definition) => buildSection({
      ...definition,
      value: definition.value(profile),
    }));
  const adaptiveSections = adaptiveGroup
    ? adaptiveGroup.sections.map((definition) => buildSection({
      ...definition,
      value: definition.value(profile),
    }))
    : [];

  return {
    needToKnowItems: buildNeedToKnowItems(profile),
    coreSections,
    adaptiveGroup: adaptiveGroup
      ? {
        key: adaptiveGroup.key,
        label: resolveAdaptiveGroupLabel(adaptiveGroup, careFocusItems),
        sections: adaptiveSections,
      }
      : null,
  };
};
